import { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@components/modals/confirm-modal/src/confirm-modal';
import { Modal } from '@components/modals/modal';
import { isMobileImplicitDismiss, type ModalCloseSource } from '@components/modals/modal-v2/src/modal-v2.types';

import { ModalContextValue, ModalTypes } from './modal-context.type';
import { ACTION_MODALS } from './utils';

export const ModalContext = createContext<ModalContextValue>({
  currentModalType: '',
  toggleModal: () => {},
  setPreventExit: () => {},
  handleCloseModal: () => {},
});

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [currentModalType, setCurrentModalType] = useState<ModalTypes>('');
  const [preventExit, setPreventExit] = useState<boolean>(false);

  const toggleModal = useCallback(
    ({ type, isOpen }: { type: ModalTypes; isOpen?: boolean }) => {
      setCurrentModalType(type);
      const shouldOpen = isOpen ?? !isModalOpen;
      setIsModalOpen(shouldOpen);

      // Reset preventExit when explicitly closing modal
      if (!shouldOpen) {
        setPreventExit(false);
      }
    },
    [isModalOpen]
  );

  const handleCloseModal = useCallback(
    (source?: ModalCloseSource) => {
      if (preventExit && !isMobileImplicitDismiss(source)) {
        setIsConfirmModalOpen(true);
      } else {
        toggleModal({ type: '', isOpen: false });
      }
    },
    [preventExit, toggleModal]
  );

  const handleConfirmModal = () => {
    setPreventExit(false);
    setIsConfirmModalOpen(false);
    toggleModal({ type: '', isOpen: false });
  };

  const ModalComponent = useCallback(() => {
    if (!currentModalType) return null;

    return ACTION_MODALS[currentModalType].component();
  }, [currentModalType]);

  const contextValue = useMemo(
    () => ({ currentModalType, toggleModal, setPreventExit, handleCloseModal }),
    [currentModalType, toggleModal, setPreventExit, handleCloseModal]
  );

  return (
    <ModalContext.Provider value={contextValue}>
      <Modal isOpen={isModalOpen} handleClose={handleCloseModal}>
        <ModalComponent />
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          title={t('offers:admissionModalConfirmTitle')}
          handleClickConfirm={handleConfirmModal}
          handleClose={() => setIsConfirmModalOpen(false)}
        />
      </Modal>
      {children}
    </ModalContext.Provider>
  );
};
